import type { Variant } from "@app/protocol";
import type { baloot, GameView, hearts, king, pisti, spades, tarneeb } from "@app/rules";
import type { ReactNode } from "react";
import { BalootCallSheet, BalootDoubleSheet, TurnedCard } from "../components/BalootPanels";
import { KingContractSheet } from "../components/KingPanels";
import { PassSheet } from "../components/PassSheet";
import { PistiPile } from "../components/PistiPile";
import type { PassChoice } from "../components/passChoice";
import type { seatSlots } from "../components/seatOrder";
import { HeartsState, KingGame, SunMode, TrumpCard } from "../components/TableGameState";
import { BidSheet, SpadesBidSheet, TrumpSheet } from "../components/TablePanels";
import type { AnyView, BiddingView } from "./types";

/**
 * A game as the table shows it (review 0027 B2): the client's side of `ENGINES`. The shared table
 * asks the module, never the game's name; the record below is exhaustive, so a game on the wire
 * without a module does not compile and none falls back to another game's panels. What a game
 * says is in `words.ts`, what it is in `facts.ts`; this file holds what it draws.
 */

export interface PanelProps<V> {
  view: V;
  names: (string | null)[];
  narrow: boolean;
  pass: PassChoice | null;
}

// Methods, not function properties: a module written for one game's view stands in the record
// of all of them, and `gameOf` hands it only views of its own game.
export interface GameModule<V extends AnyView = AnyView> {
  /** Your panel when the move is yours: a bid, the trump, a call, a pass, the hand's game. */
  Panel(props: PanelProps<V>): ReactNode;
  /** What stands in the top bar where a trump would. */
  BarState(props: { view: V }): ReactNode;
  /** In the middle of the table instead of the trick, when the game shows something else there. */
  Middle?(props: {
    view: V;
    card: { w: number; h: number };
    slots: ReturnType<typeof seatSlots>;
    names: readonly (string | null)[];
  }): ReactNode;
}

function BiddingPanel({ view, names, narrow }: PanelProps<BiddingView>) {
  if (view.phase === "bidding") return <BidSheet view={view} names={names} narrow={narrow} />;
  return view.phase === "trump" ? <TrumpSheet view={view} dense={narrow} /> : null;
}

function ContractTrump({ view }: { view: BiddingView }) {
  return <TrumpCard trump={view.contract?.trump ?? null} />;
}

const batak: GameModule<GameView> = {
  Panel: BiddingPanel,
  BarState: ContractTrump,
};

const tarneebGame: GameModule<tarneeb.TarneebView> = {
  Panel: BiddingPanel,
  BarState: ContractTrump,
};

const spadesGame: GameModule<spades.SpadesView> = {
  Panel: ({ view, narrow }) =>
    view.phase === "bidding" ? <SpadesBidSheet view={view} narrow={narrow} /> : null,
  BarState: () => <TrumpCard trump="spades" />,
};

function BalootPanel({ view, narrow }: PanelProps<baloot.BalootView>) {
  if (view.phase === "bidding") return <BalootCallSheet view={view} narrow={narrow} />;
  return view.phase === "doubling" ? <BalootDoubleSheet view={view} narrow={narrow} /> : null;
}

const balootGame: GameModule<baloot.BalootView> = {
  Panel: BalootPanel,
  BarState: ({ view }) =>
    view.contract?.mode === "sun" ? (
      <SunMode />
    ) : (
      <TrumpCard trump={view.contract?.trump ?? null} />
    ),
  // The turned card lies face up in the middle while the call goes round.
  Middle: ({ view, card }) =>
    view.phase === "bidding" ? <TurnedCard view={view} card={card} /> : null,
};

const heartsGame: GameModule<hearts.HeartsView> = {
  Panel: ({ view, names, narrow, pass }) =>
    pass === null ? null : <PassSheet view={view} names={names} choice={pass} dense={narrow} />,
  BarState: ({ view }) => <HeartsState open={view.heartsBroken} />,
};

const kingGame: GameModule<king.KingView> = {
  Panel: ({ view, narrow }) => <KingContractSheet view={view} narrow={narrow} />,
  BarState: ({ view }) => <KingGame view={view} />,
};

const pistiGame: GameModule<pisti.PistiView> = {
  // Every card is always playable and nothing is bid: the hand is the whole panel.
  Panel: () => null,
  BarState: () => null,
  // No trick to show: the pile, and between decks its count (ADR 0050).
  Middle: ({ view, card, slots, names }) => (
    <PistiPile view={view} card={card} slots={slots} names={names} />
  ),
};

type ViewOf<K extends Variant> = Extract<AnyView, { variant: K }>;

const GAMES: { readonly [K in Variant]: GameModule<ViewOf<K>> } = {
  batak,
  spades: spadesGame,
  tarneeb: tarneebGame,
  baloot: balootGame,
  hearts: heartsGame,
  king: kingGame,
  pisti: pistiGame,
};

/** The module of the game this view belongs to. */
export function gameOf(view: AnyView): GameModule {
  return GAMES[view.variant] as GameModule;
}
